<template>
  <div class="page-view">

    <BasePageHeader
      :title="isNew ? $t('wiki.newTitle') : (form.name || $t('wiki.pageTitle'))"
      @back="router.back()"
    >
      <template #actions>
        <template v-if="isNew || editing">
          <button class="btn ghost small" v-if="!isNew" @click="cancelEdit">{{ $t('common.cancel') }}</button>
          <button class="btn primary small" :disabled="saving || !formValid" @click="save">
            {{ saving ? $t('common.saving') : (isNew ? $t('common.create') : $t('common.save')) }}
          </button>
        </template>
        <template v-else>
          <button class="btn ghost small flex items-center gap-1.5" @click="editing = true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5Z"/>
            </svg>
            {{ $t('common.edit') }}
          </button>
        </template>
      </template>
    </BasePageHeader>

    <div v-if="loading" class="state-empty">{{ $t('common.loading') }}</div>
    <div v-else-if="loadError" class="state-empty error">{{ loadError }}</div>

    <template v-else>

      <!-- View mode: rendered markdown -->
      <template v-if="!isNew && !editing">
        <div class="px-5 pt-6 pb-4">
          <h1 class="text-3xl font-extrabold mb-5">{{ form.name }}</h1>
          <div class="wiki-prose" v-html="renderedContent" />
        </div>

        <div v-if="linkedItems.length" class="px-4 pt-5 pb-2 border-t border-[var(--color-border)]">
          <div class="section-label">{{ $t('wiki.linkedItems') }}</div>
          <div class="flex flex-col gap-1.5 pb-2">
            <div
              v-for="item in linkedItems"
              :key="item.id"
              class="data-card cursor-pointer hover:bg-[var(--color-surface-2)]"
              @click="$router.push(`/item/${item.id}`)"
            >
              <div class="data-card__name flex-1">{{ item.name }}</div>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 text-gray-600 flex-shrink-0">
                <path d="m9 18 6-6-6-6"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="px-4 pt-5 pb-4 border-t border-[var(--color-border)]">
          <div class="section-label danger">{{ $t('common.dangerZone') }}</div>
          <button class="btn danger w-full" :disabled="deleting" @click="showDeleteConfirm = true">
            {{ deleting ? $t('common.deleting') : $t('wiki.deletePage') }}
          </button>
        </div>
      </template>

      <!-- Edit / create mode -->
      <template v-else>
        <div class="px-4 pt-5 pb-2 border-t border-[var(--color-border)]">
          <div class="section-label">{{ $t('wiki.pageDetails') }}</div>

          <BaseFormField :label="$t('wiki.titleLabel')" required>
            <BaseInput v-model="form.name" :placeholder="$t('ui.eGKamerakonfigurasjon')" maxlength="200" />
          </BaseFormField>

          <BaseFormField :label="$t('wiki.contentLabel')" required :hint="$t('wiki.markdownHint')">
            <BaseTextarea
              class="font-mono text-sm resize-y"
              style="min-height: 400px"
              v-model="form.text"
              :placeholder="$t('wiki.contentPlaceholder')"
              :rows="24"
            />
          </BaseFormField>
        </div>
      </template>

    </template>

    <BaseConfirmModal
      :show="showDeleteConfirm"
      :title="$t('wiki.deleteConfirmTitle')"
      :body="$t('wiki.deleteConfirmBody', { name: form.name })"
      :confirm-label="$t('common.delete')"
      confirm-variant="delete"
      :loading="deleting"
      @confirm="confirmDelete"
      @cancel="showDeleteConfirm = false"
    />

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { marked } from 'marked'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import { getWikiPage, createWikiPage, updateWikiPage, deleteWikiPage, getItemsList } from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseTextarea from '@/components/base/BaseTextarea.vue'
import BaseConfirmModal from '@/components/base/BaseConfirmModal.vue'

const router = useRouter()
const route  = useRoute()
const toast  = useToast()
const { t }  = useI18n()

const wikiId = computed(() => route.params.wikiId)
const isNew  = computed(() => !wikiId.value || wikiId.value === 'new')

const form    = ref({ name: '', text: '' })
const editing = ref(false)

let savedForm = { name: '', text: '' }

const formValid       = computed(() => form.value.name.trim() && form.value.text.trim())
const renderedContent = computed(() => marked.parse(form.value.text || ''))

const loading   = ref(false)
const loadError = ref(null)
const saving    = ref(false)
const deleting  = ref(false)
const showDeleteConfirm = ref(false)
const linkedItems = ref([])

async function load() {
  if (isNew.value) { editing.value = true; return }

  loading.value   = true
  loadError.value = null
  try {
    const [page, itemsRes] = await Promise.all([
      getWikiPage(wikiId.value),
      getItemsList({ wiki_page: wikiId.value, page_size: 100 }),
    ])
    form.value.name = page.name ?? ''
    form.value.text = page.text ?? ''
    savedForm = { name: form.value.name, text: form.value.text }
    linkedItems.value = itemsRes.results ?? itemsRes ?? []
  } catch {
    loadError.value = t('wiki.loadPageFailed')
  } finally {
    loading.value = false
  }
}

function cancelEdit() {
  form.value = { ...savedForm }
  editing.value = false
}

async function save() {
  if (!formValid.value || saving.value) return
  saving.value = true
  try {
    const payload = { name: form.value.name.trim(), text: form.value.text }
    if (isNew.value) {
      const created = await createWikiPage(payload)
      toast.success(t('wiki.created'))
      router.replace(`/wiki/${created.id}`)
    } else {
      await updateWikiPage(wikiId.value, payload)
      savedForm = { name: form.value.name, text: form.value.text }
      editing.value = false
      toast.success(t('wiki.saved'))
    }
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('wiki.saveFailed'))
  } finally {
    saving.value = false
  }
}

async function confirmDelete() {
  deleting.value = true
  try {
    await deleteWikiPage(wikiId.value)
    toast.success(t('wiki.deleted'))
    router.replace('/wiki')
  } catch {
    toast.error(t('wiki.deleteFailed'))
    deleting.value = false
    showDeleteConfirm.value = false
  }
}

onMounted(load)
</script>

<style>
.wiki-prose { color: #d1d5db; line-height: 1.75; font-size: .95rem; }
.wiki-prose h1,
.wiki-prose h2,
.wiki-prose h3,
.wiki-prose h4 { color: #f9fafb; font-weight: 700; margin: 1.5rem 0 .6rem; line-height: 1.3; }
.wiki-prose h1 { font-size: 1.4rem; }
.wiki-prose h2 { font-size: 1.2rem; border-bottom: 1px solid #1f2937; padding-bottom: .4rem; }
.wiki-prose h3 { font-size: 1.05rem; }
.wiki-prose p { margin: .75rem 0; }
.wiki-prose ul,
.wiki-prose ol { padding-left: 1.5rem; margin: .75rem 0; }
.wiki-prose li { margin: .3rem 0; }
.wiki-prose code {
  background: var(--color-surface); border: 1px solid #1f2937; border-radius: .3rem;
  padding: .15rem .4rem; font-size: .85em; color: #93c5fd; font-family: ui-monospace, monospace;
}
.wiki-prose pre {
  background: #0f1720; border: 1px solid #1f2937; border-radius: .625rem;
  padding: 1rem; overflow-x: auto; margin: 1rem 0;
}
.wiki-prose pre code { background: none; border: none; padding: 0; color: #e2e8f0; }
.wiki-prose blockquote {
  border-left: 3px solid #3b82f6; padding-left: 1rem; margin: .75rem 0;
  color: #9ca3af; font-style: italic;
}
.wiki-prose a { color: #60a5fa; text-decoration: underline; }
.wiki-prose hr { border: none; border-top: 1px solid #1f2937; margin: 1.5rem 0; }
.wiki-prose table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: .9rem; }
.wiki-prose th { background: var(--color-surface); color: #9ca3af; font-weight: 600; text-align: left; padding: .5rem .75rem; border: 1px solid #1f2937; }
.wiki-prose td { padding: .5rem .75rem; border: 1px solid #1f2937; }
.wiki-prose tr:nth-child(even) { background: #0a0f18; }
.wiki-prose strong { color: #f9fafb; font-weight: 700; }
.wiki-prose em { color: #e5e7eb; }
.wiki-prose img { max-width: 100%; border-radius: .5rem; margin: .5rem 0; }
</style>
